import React, { useState } from "react";
import { useTenant } from "../context/TenantContext";
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend } from "react-icons/fi";
import toast from "react-hot-toast";
import "../styles/contact.css";

const Contact = () => {
  const { tenant } = useTenant();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
      setSending(false);
    }, 1000);
  };

  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  const getGoogleMapsUrl = () => {
    const embed = tenant?.GoogleMapsEmbed;
    if (!embed) return null;

    const srcMatch = embed.match(/src="([^"]+)"/);
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1];
    }

    if (embed.startsWith("http")) {
      return embed;
    }

    return null;
  };

  // Format business hours from JSON - INCLUDING WEEKENDS
  const getFormattedBusinessHours = () => {
    const hours = tenant?.BusinessHours;
    if (!hours) return null;

    try {
      const parsedHours = typeof hours === "string" ? JSON.parse(hours) : hours;

      // All days of the week
      const allDays = [
        { key: "monday", label: "Monday" },
        { key: "tuesday", label: "Tuesday" },
        { key: "wednesday", label: "Wednesday" },
        { key: "thursday", label: "Thursday" },
        { key: "friday", label: "Friday" },
        { key: "saturday", label: "Saturday" },
        { key: "sunday", label: "Sunday" },
      ];

      const openDays = [];

      for (const day of allDays) {
        const dayData = parsedHours[day.key];
        if (dayData?.enabled && dayData.open && dayData.close) {
          openDays.push(`${day.label}: ${dayData.open} - ${dayData.close}`);
        } else if (dayData && !dayData.enabled) {
          openDays.push(`${day.label}: Closed`);
        }
      }

      if (openDays.length === 0) {
        return "No business hours available";
      }

      return openDays;
    } catch (e) {
      console.error("Error parsing business hours:", e);
      return null;
    }
  };

  const businessHoursList = getFormattedBusinessHours();
  const mapsUrl = getGoogleMapsUrl();

  return (
    <div className="contact-page">
      <div className="container">
        <section className="contact-hero">
          <h1>Contact Us</h1>
          <p>
            We'd love to hear from you. Send us a message and we'll respond as
            soon as possible.
          </p>
        </section>

        <div className="contact-grid">
          {/* Contact Info Section */}
          <div className="contact-info-section">
            <h2>Get in Touch</h2>
            <div className="contact-details">
              {/* Address */}
              {(tenant?.AddressLine1 || tenant?.City) && (
                <div className="contact-detail">
                  <FiMapPin className="detail-icon" />
                  <div>
                    <strong>Address</strong>
                    <p>
                      {tenant?.AddressLine1 && (
                        <span>
                          {tenant.AddressLine1}
                          <br />
                        </span>
                      )}
                      {tenant?.AddressLine2 && (
                        <span>
                          {tenant.AddressLine2}
                          <br />
                        </span>
                      )}
                      {tenant?.City && (
                        <span>
                          {tenant.City}
                          <br />
                        </span>
                      )}
                      {tenant?.State && (
                        <span>
                          {tenant.State}
                          <br />
                        </span>
                      )}
                      {tenant?.PostalCode && (
                        <span>
                          {tenant.PostalCode}
                          <br />
                        </span>
                      )}
                      {tenant?.Country && <span>{tenant.Country}</span>}
                    </p>
                  </div>
                </div>
              )}

              {/* Phone */}
              {tenant?.BusinessPhone && (
                <div className="contact-detail">
                  <FiPhone className="detail-icon" />
                  <div>
                    <strong>Phone</strong>
                    <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                      {tenant.BusinessPhone}
                    </a>
                    {tenant?.AlternatePhone && (
                      <p>
                        Alt:{" "}
                        <a href={`tel:${formatTelLink(tenant.AlternatePhone)}`}>
                          {tenant.AlternatePhone}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Email */}
              {tenant?.BusinessEmail && (
                <div className="contact-detail">
                  <FiMail className="detail-icon" />
                  <div>
                    <strong>Email</strong>
                    <a href={`mailto:${tenant.BusinessEmail}`}>
                      {tenant.BusinessEmail}
                    </a>
                  </div>
                </div>
              )}

              {/* Business Hours - Now shows all days including weekends */}
              {businessHoursList && businessHoursList.length > 0 && (
                <div className="contact-detail">
                  <FiClock className="detail-icon" />
                  <div>
                    <strong>Business Hours</strong>
                    <div className="business-hours-list">
                      {businessHoursList.map((hour, index) => (
                        <p
                          key={index}
                          className={
                            hour.includes("Closed") ? "closed-day" : "open-day"
                          }
                        >
                          {hour}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-section">
            <h2>Send us a Message</h2>
            <form onSubmit={handleSubmit} className="contact-form">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
              <textarea
                name="message"
                placeholder="Your Message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              />
              <button type="submit" disabled={sending}>
                <FiSend /> {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* Google Maps Section */}
        {mapsUrl && (
          <div className="contact-map-section">
            <h2>Find Us</h2>
            <div className="map-container">
              <iframe
                src={mapsUrl}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Store Location"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contact;
